// Shinplex theme JS
/* Transparent -> opaque header on scroll.
   The header is position:fixed and transparent at rest so the hero sits
   underneath it. It floats below the announcement bar (if one is enabled)
   until the user scrolls past it, then pins to the top and goes solid. */
document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const announcement = document.querySelector('.announcement');

  const measure = () => {
    const barHeight = announcement ? announcement.offsetHeight : 0;
    document.documentElement.style.setProperty('--header-top', barHeight + 'px');
    return barHeight;
  };

  let threshold = Math.max(measure(), 24);

  const onScroll = () => header.classList.toggle('scrolled', window.scrollY > threshold);

  const onResize = () => {
    threshold = Math.max(measure(), 24);
    onScroll();
  };

  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
});
/* Stories drawer — delegated so it survives Shopify section re-renders. */
(() => {
  const MEDIA_EXT = /\.(mp4|webm|mov|m4v|ogg|ogv|m3u8)(\?|#|$)/i;

  const parseSources = (thumb) => {
    try {
      const raw = thumb.dataset.storySources;
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        return parsed.filter((s) => s && s.src);
      }
    } catch {
      /* ignore malformed JSON */
    }
    return [];
  };

  const youtubeId = (url) => {
    if (!url) return '';
    const m = url.match(
      /(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/i
    );
    return m ? m[1] : '';
  };

  const vimeoId = (url) => {
    if (!url) return '';
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    return m ? m[1] : '';
  };

  const isAdminFilePage = (url) =>
    /admin\.shopify\.com/i.test(url) || /\/content\/files\//i.test(url);

  const isDirectMedia = (url) => {
    if (!url || isAdminFilePage(url)) return false;
    if (MEDIA_EXT.test(url)) return true;
    return /cdn\.shopify\.com\/videos\//i.test(url) || /\/cdn\/shop\/videos\//i.test(url);
  };

  /* Prefer adaptive HLS, then lowest adequate progressive (480→720),
     and push huge 1080p+ progressive to the end. Browsers that cannot
     play m3u8 skip it via type= and take the next source. */
  const parseHeight = (source) => {
    const h = Number(source && source.height);
    if (h > 0) return h;
    const src = (source && source.src) || '';
    const labeled = src.match(/(?:^|[^\d])(\d{3,4})p(?:[^\d]|$)/i);
    if (labeled) return Number(labeled[1]);
    const dims = src.match(/[_-](\d{3,4})x(\d{3,4})(?:[_-]|$)/i);
    if (dims) return Math.min(Number(dims[1]), Number(dims[2]));
    return 0;
  };

  const isHlsSource = (type, src) => {
    const t = `${type || ''} ${src || ''}`.toLowerCase();
    return t.includes('mpegurl') || /\.m3u8($|\?|#)/i.test(src || '');
  };

  const sourceRank = (source) => {
    const type = (source && source.type) || '';
    const src = (source && source.src) || '';
    if (isHlsSource(type, src)) return 0;

    const height = parseHeight(source);
    const t = `${type} ${src}`.toLowerCase();
    const isProg =
      t.includes('mp4') ||
      t.includes('m4v') ||
      t.includes('webm') ||
      t.includes('quicktime') ||
      /\.mov($|\?|#)/i.test(src);

    if (isProg || height > 0) {
      if (height > 0 && height <= 480) return 10 + height / 10000;
      if (height > 480 && height <= 720) return 20 + height / 10000;
      if (height === 0) return 35;
      if (height <= 1080) return 50 + height / 10000;
      return 60 + height / 10000;
    }
    return 70;
  };

  const collectSources = (thumb) => {
    const sources = parseSources(thumb).slice();
    const url = (thumb.dataset.storyVideo || '').trim();
    if (isDirectMedia(url) && !sources.some((s) => s.src === url)) {
      let type = '';
      if (/\.mp4($|\?|#)/i.test(url) || /\.m4v($|\?|#)/i.test(url)) type = 'video/mp4';
      else if (/\.webm($|\?|#)/i.test(url)) type = 'video/webm';
      else if (/\.m3u8($|\?|#)/i.test(url)) type = 'application/x-mpegURL';
      else if (/\.mov($|\?|#)/i.test(url)) type = 'video/quicktime';
      sources.push({ src: url, type });
    }
    sources.sort((a, b) => sourceRank(a) - sourceRank(b));
    return sources;
  };

  const showPlaceholder = (media, poster, label) => {
    media.innerHTML = '';
    if (poster) {
      const img = document.createElement('img');
      img.src = poster;
      img.alt = '';
      media.appendChild(img);
    }
    const badge = document.createElement('div');
    badge.className = 'story-soon';
    badge.textContent = label;
    media.appendChild(badge);
  };

  const playVideo = (video) => {
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.controls = true;
    video.preload = 'auto';
    try {
      video.load();
    } catch {
      /* some browsers throw if src is still empty */
    }
    const attempt = video.play();
    if (!attempt || typeof attempt.catch !== 'function') return;
    attempt.catch(() => {
      video.muted = true;
      const retry = () => {
        video.play().catch(() => {
          video.controls = true;
        });
      };
      if (video.readyState >= 2) retry();
      else video.addEventListener('canplay', retry, { once: true });
    });
  };

  const mountFile = (media, sources, poster) => {
    const video = document.createElement('video');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.playsInline = true;
    video.controls = true;
    video.preload = 'auto';
    if (poster) video.poster = poster;

    sources.forEach((s) => {
      const source = document.createElement('source');
      source.src = s.src;
      if (s.type) source.type = s.type;
      video.appendChild(source);
    });
    if (sources.length === 1) video.src = sources[0].src;

    video.addEventListener('error', () => {
      if (video.networkState !== 3) return;
      showPlaceholder(media, poster, 'VIDEO UNAVAILABLE');
    });

    media.innerHTML = '';
    media.appendChild(video);
    playVideo(video);
  };

  const mountEmbed = (media, src) => {
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = 'Customer story';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.setAttribute('allowfullscreen', '');
    media.innerHTML = '';
    media.appendChild(iframe);
  };

  const stopMedia = (media) => {
    if (!media) return;
    media.querySelectorAll('video').forEach((v) => {
      try {
        v.pause();
      } catch {
        /* ignore */
      }
      v.removeAttribute('src');
      v.querySelectorAll('source').forEach((s) => s.removeAttribute('src'));
      try {
        v.load();
      } catch {
        /* ignore */
      }
    });
    media.innerHTML = '';
  };

  const thumbsOf = (section) => Array.from(section.querySelectorAll('.story-thumb'));

  const render = (section, index) => {
    const drawer = section.querySelector('[data-story-drawer]');
    if (!drawer) return;
    const thumbs = thumbsOf(section);
    if (!thumbs.length) return;
    const current = ((index % thumbs.length) + thumbs.length) % thumbs.length;
    drawer.dataset.storyIndex = String(current);
    const thumb = thumbs[current];
    const media = drawer.querySelector('[data-story-media]');
    const titleEl = drawer.querySelector('[data-story-current-title]');
    const productCard = drawer.querySelector('[data-story-product]');
    const productImg = drawer.querySelector('[data-story-product-img]');
    const productTitleEl = drawer.querySelector('[data-story-product-title]');
    const productPriceEl = drawer.querySelector('[data-story-product-price]');
    const poster = thumb.dataset.storyPoster || '';
    const url = (thumb.dataset.storyVideo || '').trim();
    const sources = collectSources(thumb);
    const yt = youtubeId(url);
    const vimeo = vimeoId(url);

    if (titleEl) titleEl.textContent = thumb.dataset.storyTitle || '';
    stopMedia(media);

    if (sources.length) {
      mountFile(media, sources, poster);
    } else if (yt) {
      mountEmbed(media, `https://www.youtube.com/embed/${yt}?autoplay=1&playsinline=1&rel=0`);
    } else if (vimeo) {
      mountEmbed(media, `https://player.vimeo.com/video/${vimeo}?autoplay=1`);
    } else {
      showPlaceholder(media, poster, 'VIDEO COMING SOON');
    }

    const purl = thumb.dataset.storyProductUrl;
    if (productCard) {
      if (purl) {
        productCard.hidden = false;
        productCard.href = purl;
        if (productImg) productImg.src = thumb.dataset.storyProductImage || '';
        if (productTitleEl) productTitleEl.textContent = thumb.dataset.storyProductTitle || '';
        if (productPriceEl) productPriceEl.textContent = thumb.dataset.storyProductPrice || '';
      } else {
        productCard.hidden = true;
      }
    }
  };

  const open = (section, index) => {
    const drawer = section.querySelector('[data-story-drawer]');
    if (!drawer) return;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    render(section, index);
  };

  const close = (section) => {
    const drawer = section.querySelector('[data-story-drawer]');
    if (!drawer) return;
    drawer.hidden = true;
    document.body.style.overflow = '';
    stopMedia(drawer.querySelector('[data-story-media]'));
  };

  const openDrawer = () => document.querySelector('.story-drawer:not([hidden])');

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('.story-thumb');
    if (thumb) {
      const section = thumb.closest('.stories-section');
      if (!section) return;
      e.preventDefault();
      const i = parseInt(thumb.dataset.storyIndex || '0', 10);
      open(section, Number.isNaN(i) ? 0 : i);
      return;
    }

    const closer = e.target.closest('[data-story-close]');
    if (closer) {
      const section = closer.closest('.stories-section');
      if (section) close(section);
      return;
    }

    const prev = e.target.closest('[data-story-prev]');
    if (prev) {
      const section = prev.closest('.stories-section');
      const drawer = section && section.querySelector('[data-story-drawer]');
      if (!drawer || drawer.hidden) return;
      render(section, parseInt(drawer.dataset.storyIndex || '0', 10) - 1);
      return;
    }

    const next = e.target.closest('[data-story-next]');
    if (next) {
      const section = next.closest('.stories-section');
      const drawer = section && section.querySelector('[data-story-drawer]');
      if (!drawer || drawer.hidden) return;
      render(section, parseInt(drawer.dataset.storyIndex || '0', 10) + 1);
    }
  });

  document.addEventListener('keydown', (e) => {
    const drawer = openDrawer();
    if (!drawer) return;
    const section = drawer.closest('.stories-section');
    if (!section) return;
    if (e.key === 'Escape') close(section);
    if (e.key === 'ArrowDown') render(section, parseInt(drawer.dataset.storyIndex || '0', 10) + 1);
    if (e.key === 'ArrowUp') render(section, parseInt(drawer.dataset.storyIndex || '0', 10) - 1);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector && e.target.querySelector('.stories-section');
    if (section) close(section);
  });
})();

/* ============================================================
   Cart drawer — ported from Lovable (CartDrawer / CartUpsells /
   CartReminder). Real Shopify cart via the AJAX Cart API; the
   drawer section is re-rendered server-side after every change
   using the Section Rendering API.
   ============================================================ */
(() => {
  if (window.__shinplexCartDrawerBound) return;
  window.__shinplexCartDrawerBound = true;

  const SECTION_ID = 'cart-drawer';

  const q = (sel, root) => (root || document).querySelector(sel);

  const getDrawer = () => q('[data-cart-drawer]');
  const getAddedSheet = () => q('[data-added-sheet]');

  const isOpen = () => {
    const d = getDrawer();
    return !!d && d.classList.contains('is-open');
  };

  const isAddedOpen = () => {
    const s = getAddedSheet();
    return !!s && s.classList.contains('is-open');
  };

  const closeAddedSheet = () => {
    const s = getAddedSheet();
    if (!s) return;
    s.classList.remove('is-open');
    s.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('added-sheet-open');
    document.body.classList.remove('added-sheet-open');
  };

  const restoreCheckoutOverlays = () => {
    document.querySelectorAll('[data-cart-pe-none]').forEach((el) => {
      el.removeAttribute('data-cart-pe-none');
    });
  };

  const pointInRect = (x, y, r) =>
    x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

  /* Native POST /cart name=checkout, with /checkout fallback. */
  const goToCheckout = (btn) => {
    if (!btn || btn.disabled) return;
    const form = btn.form || btn.closest('form');
    if (form && typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit(btn);
        return;
      } catch (err) {
        /* fall through */
      }
    }
    if (form) {
      let input = form.querySelector('input[name="checkout"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout';
        input.value = 'Checkout';
        form.appendChild(input);
      }
      form.submit();
      return;
    }
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.assign(`${String(root).replace(/\/?$/, '/')}checkout`);
  };

  /* Loyalty / chat launchers sit on the CHECKOUT CTA and steal clicks.
     Punch pointer-events through those only — wallet overflow is handled by
     the capture-phase checkout retargeter (pe-none on the wallet root would
     kill Shop Pay below). */
  const passThroughCheckoutOverlays = () => {
    const d = getDrawer();
    const btn = d && q('[data-cart-checkout]', d);
    if (!btn || !d.classList.contains('is-open')) return;
    const r = btn.getBoundingClientRect();
    const samples = [
      [r.right - 8, r.top + r.height / 2],
      [r.right - 8, r.bottom - 8],
      [r.left + r.width * 0.85, r.top + r.height / 2],
      [r.left + r.width / 2, r.top + r.height / 2],
    ];
    samples.forEach(([x, y]) => {
      if (x < 0 || y < 0) return;
      document.elementsFromPoint(x, y).some((el) => {
        if (btn === el || btn.contains(el)) return true;
        if (el.closest && el.closest('[data-discount-form]')) return true;
        if (el.closest && el.closest('[data-cart-dynamic-checkout]')) return false;
        if (el === document.body || el === document.documentElement) return false;
        if (el.hasAttribute && el.hasAttribute('data-cart-close')) return false;
        const pos = window.getComputedStyle(el).position;
        if (el.tagName !== 'IFRAME' && pos !== 'fixed' && pos !== 'absolute' && pos !== 'sticky') {
          return false;
        }
        el.setAttribute('data-cart-pe-none', '');
        return false;
      });
    });
  };

  const bindDrawerChrome = (d) => {
    const body = q('.cart-drawer__body', d);
    if (!body || body.dataset.chromeBound === '1') return;
    body.dataset.chromeBound = '1';
    const onScroll = () => {
      d.classList.toggle('is-chrome-hidden', body.scrollTop > 12);
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  const bindDrawerSwipe = (d) => {
    const panel = q('[data-cart-panel], .cart-drawer__panel', d);
    if (!panel || panel.dataset.swipeBound === '1') return;
    panel.dataset.swipeBound = '1';
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let dragging = false;
    const EDGE = 28;
    const reset = () => {
      panel.classList.remove('is-swiping');
      panel.style.transform = '';
    };
    panel.addEventListener(
      'touchstart',
      (e) => {
        if (!e.touches || !e.touches[0]) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        dx = 0;
        dragging = true;
      },
      { passive: false }
    );
    panel.addEventListener(
      'touchmove',
      (e) => {
        if (!dragging || !e.touches || !e.touches[0]) return;
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        dx = x - startX;
        const dy = y - startY;
        const fromEdge = startX <= EDGE;
        const horizontal = Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8;
        if (fromEdge || horizontal) e.preventDefault();
        if (dx > 0 && (fromEdge || horizontal)) {
          panel.classList.add('is-swiping');
          panel.style.transform = `translateX(${dx}px)`;
        }
      },
      { passive: false }
    );
    panel.addEventListener(
      'touchend',
      () => {
        if (!dragging) return;
        dragging = false;
        const shouldClose = dx > 72;
        reset();
        if (shouldClose) closeDrawer();
      },
      { passive: true }
    );
    panel.addEventListener('touchcancel', () => {
      dragging = false;
      reset();
    });
  };

  const openDrawer = () => {
    closeAddedSheet();
    const d = getDrawer();
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('cart-drawer-open');
    dismissReminder();
    applyUiBundle();
    bindDrawerChrome(d);
    bindDrawerSwipe(d);
    const panel = q('[data-cart-panel], .cart-drawer__panel', d);
    if (panel) panel.focus({ preventScroll: true });
    requestAnimationFrame(passThroughCheckoutOverlays);
  };

  const closeDrawer = () => {
    const d = getDrawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');
    d.classList.remove('is-chrome-hidden');
    const panel = q('[data-cart-panel], .cart-drawer__panel', d);
    if (panel) {
      panel.classList.remove('is-swiping');
      panel.style.transform = '';
    }
    restoreCheckoutOverlays();
  };

  const updateHeaderCount = ({ bump } = {}) => {
    const d = getDrawer();
    if (!d) return;
    const count = parseInt(d.dataset.count || '0', 10);
    document.querySelectorAll('[data-cart-count], .cart-bubble .count').forEach((el) => {
      el.textContent = d.dataset.count;
    });
    document.querySelectorAll('.cart-bubble').forEach((el) => {
      const badge = el.querySelector('.count');
      if (bump && count > 0) {
        el.classList.remove('cart-nudge');
        if (badge) badge.classList.remove('cart-badge-pulse');
        void el.offsetWidth;
      }
      el.classList.toggle('cart-nudge', count > 0);
      if (badge) badge.classList.toggle('cart-badge-pulse', count > 0);
    });
  };

  const money = (cents, currency) => {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return `£${(cents / 100).toFixed(2)}`;
    }
  };

  /* Match Lovable FreeShippingBar: remaining = max(0, threshold - displayedSubtotal). */
  const updateFreeshipBar = (displayedCents) => {
    const d = getDrawer();
    if (!d) return;
    const wrap = q('[data-freeship]', d);
    if (!wrap) return;
    const threshold = parseInt(d.dataset.freeShipThreshold || '0', 10);
    if (threshold <= 0) return;
    const remaining = Math.max(0, threshold - displayedCents);
    const pct = Math.min(100, Math.round((displayedCents * 100) / threshold));
    const unlocked = remaining === 0;
    const currency = d.dataset.currency || 'GBP';
    const msg = q('[data-freeship-msg]', wrap);
    const bar = q('[data-freeship-bar]', wrap);
    if (msg) {
      if (unlocked) {
        msg.classList.add('is-unlocked');
        msg.innerHTML =
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg> FREE UK SHIPPING UNLOCKED';
      } else {
        msg.classList.remove('is-unlocked');
        msg.innerHTML = `ADD <strong>${money(remaining, currency)}</strong> MORE FOR FREE UK SHIPPING`;
      }
    }
    if (bar) {
      bar.classList.toggle('is-unlocked', unlocked);
      bar.style.width = `${pct}%`;
    }
    d.dataset.remaining = String(remaining);
  };

  /* Split automatic bundle vs discount-code savings. Never treat them as
     alternatives — both rows can show at once when both apply. */
  const splitCartSavings = (cart) => {
    const codes = new Set(
      (cart.discount_codes || [])
        .map((c) => String(c.code || '').trim().toUpperCase())
        .filter(Boolean)
    );
    let bundle = 0;
    let code = 0;
    const seen = new Set();
    const addApp = (a) => {
      if (!a) return;
      const title = String(a.title || a.description || a.code || '');
      const amt = Number(a.total_allocated_amount);
      const key = `${title}:${Number.isFinite(amt) ? amt : 0}`;
      if (seen.has(key)) return;
      seen.add(key);
      const amount = Number.isFinite(amt) ? amt : 0;
      const isCode =
        codes.has(title.toUpperCase()) || a.type === 'discount_code';
      const isBundle = /bundle guards/i.test(title) && !isCode;
      if (isBundle) bundle += amount;
      else if (isCode) code += amount;
    };
    (cart.discount_applications || []).forEach(addApp);
    (cart.cart_level_discount_applications || []).forEach(addApp);
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || item.discount_allocations || []).forEach((alloc) => {
        const app = alloc.discount_application || alloc;
        const title = String(app.title || alloc.title || '');
        if (!/bundle guards/i.test(title)) return;
        if (codes.has(title.toUpperCase()) || app.type === 'discount_code') return;
        const amt = Number(alloc.amount);
        if (Number.isFinite(amt) && amt > 0 && bundle <= 0) bundle += amt;
      });
    });
    if (code <= 0) {
      (cart.discount_codes || []).forEach((c) => {
        const raw = c.amount;
        const n = typeof raw === 'number' ? raw : parseFloat(raw);
        if (!Number.isFinite(n) || n <= 0) return;
        code += n >= 100 || Number.isInteger(n) ? Math.round(n) : Math.round(n * 100);
      });
    }
    return { bundle, code };
  };

  /* Cart totals come from Liquid (real Shopify discounts). Never invent a
     bundle-saving row — empty / one-sided carts must not show it. Code row
     is independent of the bundle row. */
  const applyUiBundle = () => {
    const d = getDrawer();
    if (!d) return;
    const count = parseInt(d.dataset.count || '0', 10);
    const bundleAmount = parseInt(d.dataset.bundleSaving || '0', 10);
    const codeAmount = parseInt(d.dataset.codeSaving || '0', 10);
    const hasPair = d.dataset.hasBundlePair === 'true';
    const bundleRow = q('[data-ui-bundle-saving]', d);
    if (bundleRow && (count === 0 || !hasPair || bundleAmount <= 0)) {
      bundleRow.hidden = true;
      bundleRow.remove();
    }
    d.querySelectorAll('[data-ui-code-saving]').forEach((row) => {
      if (count === 0 || codeAmount <= 0) {
        row.hidden = true;
        row.remove();
      }
    });
    updateFreeshipBar(parseInt(d.dataset.subtotalRaw || '0', 10));
  };

  /* Re-fetch the rendered section and swap it in, preserving open state. */
  const refreshDrawer = async ({ bump } = {}) => {
    const res = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`);
    if (!res.ok) return;
    const data = await res.json();
    const html = data[SECTION_ID];
    if (!html) return;
    const tpl = document.createElement('div');
    tpl.innerHTML = html;
    const fresh = q('[data-cart-drawer]', tpl);
    const current = getDrawer();
    if (!fresh || !current) return;
    const wasOpen = isOpen();
    const prevCount = parseInt(current.dataset.count || '0', 10);
    const prevByKey = new Map();
    current.querySelectorAll('[data-line-item]').forEach((el) => {
      prevByKey.set(el.dataset.groupId || el.dataset.key, el.dataset.qty);
    });
    current.replaceWith(fresh);
    if (wasOpen) {
      fresh.classList.add('is-open');
      fresh.setAttribute('aria-hidden', 'false');
      fresh.querySelectorAll('[data-line-item]').forEach((el) => {
        const prevQty = prevByKey.get(el.dataset.groupId || el.dataset.key);
        if (prevQty == null) el.classList.add('is-new');
        else if (String(prevQty) !== String(el.dataset.qty)) el.classList.add('is-updated');
      });
      requestAnimationFrame(passThroughCheckoutOverlays);
      bindDrawerChrome(fresh);
      bindDrawerSwipe(fresh);
      const panel = q('[data-cart-panel], .cart-drawer__panel', fresh);
      if (panel) panel.focus({ preventScroll: true });
    }
    const newCount = parseInt(fresh.dataset.count || '0', 10);
    updateHeaderCount({ bump: bump || newCount > prevCount });
    applyUiBundle();
  };

  const busy = (on) => {
    const d = getDrawer();
    if (d) d.classList.toggle('is-busy', on);
  };

  /* ---------------- Cart API ---------------- */

  /* Qty +/- must CHANGE the existing line. Never POST /cart/add.js — that
     creates a second row when the line has unique properties, a selling
     plan, or a different automatic-discount price. Prefer `line` (1-based
     cart index) so a colon-key cannot be parsed as a variant id.

     Automatic 1:1 bundle discounts can split the same variant into extra
     lines. The drawer display-groups those; mutations still target real
     sibling lines (peel/add the last extra first) so the discount stays. */
  let lineMutating = false;

  const cartApiRoot = () => {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return String(root).replace(/\/?$/, '/');
  };

  const parseGroup = (row) => {
    if (!row) return null;
    const keys = String(row.getAttribute('data-group-keys') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!keys.length) return null;
    const lines = String(row.getAttribute('data-group-lines') || '')
      .split(',')
      .map((n) => parseInt(n, 10));
    const qtys = String(row.getAttribute('data-group-qtys') || '')
      .split(',')
      .map((n) => parseInt(n, 10));
    const total = qtys.reduce((sum, n) => sum + (Number.isFinite(n) ? n : 0), 0);
    return { keys, lines, qtys, total };
  };

  const afterCartMutation = async () => {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
      return;
    }
    await refreshDrawer();
  };

  const changeLine = async ({ key, line, quantity }) => {
    if (lineMutating) return;
    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty < 0) return;
    const lineNum = parseInt(line, 10);
    const lineKey = key != null && String(key).trim() ? String(key).trim() : '';
    const payload = { quantity: qty };
    if (Number.isFinite(lineNum) && lineNum > 0) {
      payload.line = lineNum;
    } else if (lineKey) {
      payload.id = lineKey;
    } else {
      return;
    }
    lineMutating = true;
    busy(true);
    try {
      const res = await fetch(`${cartApiRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      if (!res.ok && lineKey) {
        await fetch(`${cartApiRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: { [lineKey]: qty } }),
        });
      }
      await afterCartMutation();
    } finally {
      lineMutating = false;
      busy(false);
    }
  };

  const updateLines = async (updates) => {
    if (lineMutating) return;
    lineMutating = true;
    busy(true);
    try {
      await fetch(`${cartApiRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });
      await afterCartMutation();
    } finally {
      lineMutating = false;
      busy(false);
    }
  };

  const clearCart = async () => {
    if (lineMutating) return;
    lineMutating = true;
    busy(true);
    try {
      await fetch(`${cartApiRoot()}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
      });
      await afterCartMutation();
    } finally {
      lineMutating = false;
      busy(false);
    }
  };

  /* Grouped row: targetQty is the summed quantity the UI should reach.
     +/- still uses /cart/change.js + 1-based line index on one sibling.
     Extra unbundled units sit last — peel/add those first so the 1:1
     pair (and £36.99 discount) stays. x zeroes every sibling. */
  const changeGroup = async (row, targetQty) => {
    const qty = parseInt(targetQty, 10);
    if (!Number.isFinite(qty) || qty < 0) return;
    const group = parseGroup(row);
    if (!group) {
      return changeLine({
        key: row && (row.getAttribute('data-line-key') || row.getAttribute('data-key')),
        line: row && row.getAttribute('data-line'),
        quantity: qty,
      });
    }
    if (group.keys.length === 1) {
      return changeLine({
        key: group.keys[0],
        line: group.lines[0],
        quantity: qty,
      });
    }
    const current = group.total;
    if (qty === current) return;
    if (qty <= 0) {
      const updates = {};
      group.keys.forEach((k) => {
        updates[k] = 0;
      });
      return updateLines(updates);
    }
    if (qty > current) {
      const i = group.keys.length - 1;
      return changeLine({
        key: group.keys[i],
        line: group.lines[i],
        quantity: group.qtys[i] + (qty - current),
      });
    }
    const i = group.keys.length - 1;
    const need = current - qty;
    if (need <= group.qtys[i]) {
      return changeLine({
        key: group.keys[i],
        line: group.lines[i],
        quantity: group.qtys[i] - need,
      });
    }
    let remaining = need;
    const updates = {};
    for (let j = group.keys.length - 1; j >= 0 && remaining > 0; j -= 1) {
      const take = Math.min(group.qtys[j], remaining);
      updates[group.keys[j]] = group.qtys[j] - take;
      remaining -= take;
    }
    return updateLines(updates);
  };

  const normalizeAddedItem = (data) => {
    if (!data) return null;
    if (Array.isArray(data.items) && data.items.length) return data.items[0];
    if (data.product_title || data.title) return data;
    return null;
  };

  const showAddedSheet = (item) => {
    const s = getAddedSheet();
    if (!s || !item) {
      openDrawer();
      return;
    }
    dismissReminder();
    const d = getDrawer();
    const count = parseInt((d && d.dataset.count) || '0', 10);
    const currency = (d && d.dataset.currency) || 'GBP';
    const title = item.product_title || item.title || '';
    let variant = item.variant_title || '';
    if (!variant || variant === 'Default Title') variant = '';
    const cents = parseInt(item.final_price != null ? item.final_price : item.price, 10);
    let img = '';
    if (typeof item.image === 'string') img = item.image;
    else if (item.image && item.image.src) img = item.image.src;
    else if (item.featured_image) {
      img =
        typeof item.featured_image === 'string'
          ? item.featured_image
          : item.featured_image.url || item.featured_image.src || '';
    }
    const imgEl = q('[data-added-img]', s);
    if (imgEl) {
      imgEl.hidden = !img;
      imgEl.src = img || '';
      imgEl.alt = title || 'Product added to bag';
    }
    const titleEl = q('[data-added-title]', s);
    if (titleEl) titleEl.textContent = title;
    const variantEl = q('[data-added-variant]', s);
    if (variantEl) {
      variantEl.textContent = variant;
      variantEl.hidden = !variant;
    }
    const priceEl = q('[data-added-price]', s);
    if (priceEl) priceEl.textContent = Number.isFinite(cents) ? money(cents, currency) : '';
    const viewEl = q('[data-added-view]', s);
    if (viewEl) viewEl.textContent = `View Bag (${count})`;
    s.classList.add('is-open');
    s.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('added-sheet-open');
    document.body.classList.add('added-sheet-open');
    const x = q('.added-sheet__x', s);
    if (x) x.focus();
  };

  const afterSuccessfulAdd = async (data) => {
    await refreshDrawer({ bump: true });
    if (isOpen()) return;
    showAddedSheet(normalizeAddedItem(data));
  };

  const addToCart = async (body) => {
    busy(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      const data = await res.json().catch(() => ({}));
      await afterSuccessfulAdd(data);
      return true;
    } catch (e) {
      console.error('[cart]', e);
      alert(e.message);
      return false;
    } finally {
      busy(false);
    }
  };

  const showDiscountError = (message) => {
    const d = getDrawer();
    const err = d && q('[data-discount-error]', d);
    if (!err) return;
    err.hidden = !message;
    err.textContent = message || '';
  };

  const discountCodeApplied = (cart, code) => {
    if (!cart || !code) return false;
    const needle = String(code).trim().toUpperCase();
    const codes = cart.discount_codes || [];
    if (
      codes.some(
        (c) => String(c.code || '').toUpperCase() === needle && c.applicable === true
      )
    ) {
      return true;
    }
    const apps = []
      .concat(cart.cart_level_discount_applications || [])
      .concat(cart.discount_applications || []);
    return apps.some((a) => {
      const title = String(a.title || a.code || '').toUpperCase();
      if (title !== needle) return false;
      return !a.type || a.type === 'discount_code';
    });
  };

  const listedDiscountCodes = (root) =>
    Array.from((root || document).querySelectorAll('[data-discount-remove]'))
      .map((el) => (el.dataset.code || '').trim())
      .filter(Boolean);

  const applyDiscountCode = async (raw) => {
    const code = String(raw || '').trim();
    const d = getDrawer();
    if (!code) {
      showDiscountError('Enter a discount code');
      return;
    }
    showDiscountError('');
    const existing = listedDiscountCodes(d);
    const merged = existing.slice();
    if (!merged.some((c) => c.toUpperCase() === code.toUpperCase())) merged.push(code);
    busy(true);
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      const cart = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(cart.description || cart.message || 'Could not apply discount code');
      }
      let applied = discountCodeApplied(cart, code);
      if (!applied) {
        await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
        const cart2 = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
        applied = discountCodeApplied(cart2, code);
      }
      if (!applied) throw new Error('Enter a valid discount code');
      await refreshDrawer();
    } catch (e) {
      console.error('[cart discount]', e);
      showDiscountError(e.message || 'Enter a valid discount code');
    } finally {
      busy(false);
    }
  };

  const removeDiscountCode = async (code) => {
    const remaining = listedDiscountCodes(getDrawer()).filter(
      (c) => c.toUpperCase() !== String(code || '').trim().toUpperCase()
    );
    showDiscountError('');
    busy(true);
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: remaining.join(',') }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not remove discount code');
      }
      await refreshDrawer();
    } catch (e) {
      console.error('[cart discount]', e);
      showDiscountError(e.message || 'Could not remove discount code');
    } finally {
      busy(false);
    }
  };

  /* -------- In-drawer option pickers (nudge + recs) -------- */

  const resolveVariant = (uid) => {
    const jsonEl = q(`[data-picker-variants="${uid}"]`);
    if (!jsonEl) return null;
    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch {
      return null;
    }
    if (variants.length === 1) return variants[0];
    const picker = q(`[data-picker="${uid}"]`);
    if (!picker) return variants[0] || null;
    const chosen = [];
    picker.querySelectorAll('.cart-option-picker__group').forEach((g) => {
      const checked = g.querySelector('input:checked');
      chosen.push(checked ? checked.value : null);
    });
    return (
      variants.find((v) =>
        chosen.every((val, i) => val === null || v.options[i] === val)
      ) || null
    );
  };

  const getPickerVariants = (uid) => {
    const jsonEl = q(`[data-picker-variants="${uid}"]`);
    if (!jsonEl) return [];
    try {
      return JSON.parse(jsonEl.textContent) || [];
    } catch {
      return [];
    }
  };

  const updatePickerAvailability = (picker) => {
    if (!picker) return;
    const uid = picker.dataset.picker;
    const variants = getPickerVariants(uid);
    if (!variants.length) return;
    const groups = [...picker.querySelectorAll('.cart-option-picker__group')];
    const chosen = groups.map((g) => {
      const checked = g.querySelector('input:checked');
      return checked ? checked.value : null;
    });
    groups.forEach((group, optIndex) => {
      group.querySelectorAll('.cart-option-picker__chip, .cart-option-picker__swatch').forEach((wrap) => {
        const input = wrap.querySelector('input');
        if (!input) return;
        const test = chosen.slice();
        test[optIndex] = input.value;
        const ok = variants.some(
          (v) =>
            v.available !== false &&
            (v.options || []).every((val, i) => test[i] == null || val === test[i])
        );
        wrap.classList.toggle('is-unavailable', !ok);
        wrap.setAttribute('aria-disabled', ok ? 'false' : 'true');
        input.disabled = !ok;
      });
    });
  };

  const ensurePickerAvailable = (picker) => {
    if (!picker) return null;
    const uid = picker.dataset.picker;
    const variants = getPickerVariants(uid);
    let variant = resolveVariant(uid);
    if (variant && variant.available !== false) {
      updatePickerAvailability(picker);
      return variant;
    }
    const groups = [...picker.querySelectorAll('.cart-option-picker__group')];
    const chosen = groups.map((g) => {
      const checked = g.querySelector('input:checked');
      return checked ? checked.value : null;
    });
    const next =
      variants
        .filter((v) => v.available !== false)
        .sort((a, b) => {
          const sa = (a.options || []).reduce((n, val, i) => n + (val === chosen[i] ? 1 : 0), 0);
          const sb = (b.options || []).reduce((n, val, i) => n + (val === chosen[i] ? 1 : 0), 0);
          return sb - sa;
        })[0] || variant;
    if (next) {
      groups.forEach((group, i) => {
        const val = next.options && next.options[i];
        if (val == null) return;
        group.querySelectorAll('input').forEach((input) => {
          const on = input.value === val;
          input.checked = on;
          const wrap = input.closest('.cart-option-picker__chip, .cart-option-picker__swatch');
          if (wrap) wrap.classList.toggle('is-active', on);
        });
        const sel = group.querySelector('[data-picker-selected]');
        const wrap = group.querySelector('.cart-option-picker__chip.is-active, .cart-option-picker__swatch.is-active');
        if (sel) sel.textContent = (wrap && wrap.getAttribute('data-display-name')) || val;
      });
    }
    updatePickerAvailability(picker);
    return next;
  };

  const variantImageSrc = (v) => {
    if (!v || !v.featured_image) return '';
    if (typeof v.featured_image === 'string') return v.featured_image;
    return v.featured_image.src || '';
  };

  const syncPickerCard = (picker) => {
    if (!picker) return;
    const variant = ensurePickerAvailable(picker);
    const card = picker.closest('[data-nudge], [data-rec]');
    if (!card || !variant) return;

    const img = card.querySelector('[data-card-img]');
    const src = variantImageSrc(variant);
    if (img && src) img.src = src;

    const addBtn = card.querySelector(`[data-rec-add][data-variants-for="${picker.dataset.picker}"]`);
    if (addBtn) {
      const ok = variant.available !== false;
      addBtn.disabled = !ok;
      addBtn.classList.toggle('is-sold-out', !ok);
      if (!ok) addBtn.textContent = 'SOLD OUT';
    }

    if (!card.hasAttribute('data-nudge')) return;
    const savings = parseInt(card.dataset.nudgeSavings || '0', 10);
    const currency = card.dataset.nudgeCurrency || 'GBP';
    const compare = parseInt(variant.price, 10);
    if (Number.isNaN(compare)) return;
    const sale = Math.max(0, compare - savings);
    card.querySelectorAll('[data-nudge-compare]').forEach((el) => {
      el.textContent = money(compare, currency);
    });
    card.querySelectorAll('[data-nudge-sale]').forEach((el) => {
      el.textContent = money(sale, currency);
    });
  };

  /**
   * After expanding a drawer card (bundle nudge / rec), scroll it into the
   * cart-drawer__body scroller so colour/size/add aren't clipped by the foot.
   * Scrolls only the drawer body — not window. Footer is a flex sibling, so
   * body height already excludes it; we only need the card inside that viewport.
   */
  const scrollExpandedCardIntoView = (card) => {
    if (!card) return;
    const scroller = card.closest('.cart-drawer__body');
    if (!scroller) return;
    const run = () => {
      const c = card.getBoundingClientRect();
      const s = scroller.getBoundingClientRect();
      if (c.height < 1 || s.height < 1) return;
      const pad = 12;
      const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      const st = scroller.scrollTop;
      /* Prefer center in the body viewport */
      let next = st + (c.top - s.top) - (s.height - c.height) / 2;
      /* Always keep bottom (add CTA) above the foot edge */
      const minForBottom = st + (c.bottom - s.bottom) + pad;
      if (next < minForBottom) next = minForBottom;
      /* When the card fits, also keep its top visible */
      if (c.height < s.height - pad * 2) {
        const maxForTop = st + (c.top - s.top) - pad;
        if (next > maxForTop) next = maxForTop;
      }
      next = Math.max(0, Math.min(next, maxScroll));
      if (Math.abs(next - st) < 2) return;
      scroller.scrollTo({ top: next, behavior: 'smooth' });
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  };

  /* ---------------- Event delegation ---------------- */

  /* Capture-phase: qty +/- and remove must win over any later add.js
     listener (apps, a second theme.js). Document delegation — do not
     rebind per-button after Section Rendering API replaceWith. */
  document.addEventListener(
    'click',
    (e) => {
      const qtyBtn = e.target.closest && e.target.closest('[data-qty-change]');
      const rmBtn = e.target.closest && e.target.closest('[data-cart-remove]');
      if (!qtyBtn && !rmBtn) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      const el = qtyBtn || rmBtn;
      const row = el.closest('[data-line-item], .cart-line');
      const targetQty = qtyBtn ? el.getAttribute('data-qty') : 0;
      if (row && row.getAttribute('data-group-keys')) {
        changeGroup(row, targetQty);
      } else {
        changeLine({
          key: el.getAttribute('data-line-key') || el.getAttribute('data-key'),
          line: el.getAttribute('data-line'),
          quantity: targetQty,
        });
      }
    },
    true
  );

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-added-close]')) {
      e.preventDefault();
      closeAddedSheet();
      return;
    }
    if (e.target.closest('[data-added-view]')) {
      e.preventDefault();
      closeAddedSheet();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-added-checkout]')) {
      e.preventDefault();
      const btn = q('[data-cart-checkout]', getDrawer());
      if (btn) goToCheckout(btn);
      else {
        const root =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.assign(`${String(root).replace(/\/?$/, '/')}checkout`);
      }
      return;
    }
    const opener = e.target.closest('[data-cart-open]');
    if (opener) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-clear]')) {
      e.preventDefault();
      clearCart();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      const continueLink = e.target.closest('a.cart-drawer__cta[href]');
      closeDrawer();
      if (continueLink) {
        const href = continueLink.getAttribute('href');
        if (href) {
          let dest;
          try {
            dest = new URL(href, window.location.origin);
          } catch (err) {
            window.location.assign(href);
            return;
          }
          const here = window.location.pathname.replace(/\/+$/, '') || '/';
          const there = dest.pathname.replace(/\/+$/, '') || '/';
          const id = dest.hash ? dest.hash.slice(1) : '';
          const target = id ? document.getElementById(id) : null;
          if (here === there && target) {
            requestAnimationFrame(() => {
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
            if (history.pushState) history.pushState(null, '', dest.hash);
            return;
          }
          window.location.assign(href);
        }
      }
      return;
    }
    const toggle = e.target.closest('[data-rec-toggle]');
    if (toggle) {
      const card = toggle.closest('[data-rec], [data-nudge]');
      const opts = card && card.querySelector('[data-rec-options]');
      if (opts) {
        const show = opts.hidden;
        opts.hidden = !show;
        toggle.setAttribute('aria-expanded', String(show));
        toggle.classList.toggle('is-open', show);
        if (card.hasAttribute('data-rec')) card.classList.toggle('is-expanded', show);
        if (show) scrollExpandedCardIntoView(card);
      }
      return;
    }
    const add = e.target.closest('[data-rec-add]');
    if (add) {
      if (add.disabled || add.classList.contains('is-sold-out')) return;
      const variant = resolveVariant(add.dataset.variantsFor);
      if (!variant) {
        alert('Please choose your options.');
        return;
      }
      if (variant.available === false) {
        alert('That option is sold out.');
        return;
      }
      addToCart({ id: variant.id, quantity: 1 });
      return;
    }
    const rmCode = e.target.closest('[data-discount-remove]');
    if (rmCode) {
      e.preventDefault();
      removeDiscountCode(rmCode.dataset.code);
    }
  });

  /* Keep picker visuals, selected label, thumbnail, and bundle prices in sync. */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('input');
    if (!input || !input.closest('.cart-option-picker')) return;
    if (input.disabled) return;
    const group = input.closest('.cart-option-picker__group');
    const picker = input.closest('.cart-option-picker');
    if (!group || !picker) return;
    group.querySelectorAll('.cart-option-picker__chip, .cart-option-picker__swatch').forEach((c) =>
      c.classList.toggle('is-active', c.contains(input))
    );
    const sel = group.querySelector('[data-picker-selected]');
    if (sel) {
      const wrap = input.closest('[data-display-name]');
      sel.textContent = (wrap && wrap.dataset.displayName) || input.value;
    }
    syncPickerCard(picker);
  });

  document.querySelectorAll('.cart-option-picker').forEach((picker) => syncPickerCard(picker));
  document.addEventListener('shinplex:cart-updated', () => {
    document.querySelectorAll('.cart-option-picker').forEach((picker) => syncPickerCard(picker));
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (isAddedOpen()) {
      closeAddedSheet();
      return;
    }
    if (isOpen()) closeDrawer();
  });

  /* Orange CHECKOUT: any click in its box (loyalty badge, wallet overflow, etc.)
     must checkout. Wallet clicks only pass when they fall in the wallet's own box
     below CHECKOUT — not when Shop Pay's hit area covers the orange CTA. */
  document.addEventListener(
    'click',
    (e) => {
      const d = getDrawer();
      if (!d || !d.classList.contains('is-open')) return;
      if (
        e.target.closest &&
        e.target.closest('.contact-form, #ContactPageForm, #ClubEnquiryForm, .contact-form__inner')
      ) {
        return;
      }
      const btn = q('[data-cart-checkout]', d);
      if (!btn || btn.disabled) return;
      const r = btn.getBoundingClientRect();
      const overBtn = pointInRect(e.clientX, e.clientY, r);
      if (!overBtn) return;

      const wallet = e.target.closest && e.target.closest('[data-cart-dynamic-checkout]');
      if (wallet) {
        const wr = wallet.getBoundingClientRect();
        /* Real wallet tap below the CTA — leave Shop Pay alone. */
        if (pointInRect(e.clientX, e.clientY, wr) && e.clientY > r.bottom) return;
      }

      e.preventDefault();
      e.stopPropagation();
      goToCheckout(btn);
    },
    true
  );

  /* Intercept every add-to-cart form (PDP, spec sheet, bundle offer) so it
     adds via AJAX and opens the drawer instead of navigating to /cart.
     Never intercept native checkout (POST /cart name=checkout). */
  document.addEventListener('submit', (e) => {
    if (e.submitter && e.submitter.getAttribute('name') === 'checkout') return;
    if (
      e.submitter &&
      (e.submitter.classList.contains('shopify-payment-button__button') ||
        (e.submitter.closest &&
          e.submitter.closest(
            'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button, [data-dynamic-checkout], [data-cart-dynamic-checkout]'
          )))
    ) {
      return;
    }
    if (e.target && e.target.classList && e.target.classList.contains('cart-drawer__checkout-form')) return;
    /* Never intercept Shopify contact / customer forms (POST /contact). */
    const submitForm =
      e.target && e.target.closest ? e.target.closest('form') : e.target;
    if (submitForm) {
      const action = (submitForm.getAttribute && submitForm.getAttribute('action')) || '';
      const typeEl = submitForm.querySelector && submitForm.querySelector('input[name="form_type"]');
      const formType = typeEl && typeEl.value;
      if (
        action.indexOf('/contact') !== -1 ||
        formType === 'contact' ||
        formType === 'customer' ||
        submitForm.id === 'ContactPageForm' ||
        submitForm.id === 'ClubEnquiryForm' ||
        (submitForm.classList && submitForm.classList.contains('contact-form__inner'))
      ) {
        return;
      }
    }
    const discountForm = e.target && e.target.closest ? e.target.closest('[data-discount-form]') : null;
    if (discountForm) {
      e.preventDefault();
      const input = q('[data-discount-input]', discountForm);
      applyDiscountCode(input && input.value);
      return;
    }
    if (
      e.submitter &&
      (e.submitter.hasAttribute('data-qty-change') || e.submitter.hasAttribute('data-cart-remove'))
    ) {
      e.preventDefault();
      return;
    }
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form || form.hasAttribute('data-no-ajax')) return;
    e.preventDefault();
    busy(true);
    fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Could not add to cart');
        }
        const data = await res.json().catch(() => ({}));
        await afterSuccessfulAdd(data);
      })
      .catch((err) => alert(err.message))
      .finally(() => busy(false));
  });

  /* ---------------- Cart reminder toast ---------------- */

  const REMINDER_KEY = 'shinplex.reminder.lastShown';
  let reminderTimer = null;
  let toastEl = null;

  function dismissReminder() {
    if (toastEl) {
      toastEl.remove();
      toastEl = null;
    }
  }

  const fireReminder = () => {
    const d = getDrawer();
    if (!d || d.dataset.reminder !== 'true') return;
    const count = parseInt(d.dataset.count || '0', 10);
    if (count === 0 || isOpen() || isAddedOpen()) return;
    if (window.location.pathname.includes('/cart')) return;
    const cooldownMs = parseInt(d.dataset.reminderCooldown || '5', 10) * 60000;
    const last = Number(sessionStorage.getItem(REMINDER_KEY) || '0');
    if (Date.now() - last < cooldownMs) return;
    sessionStorage.setItem(REMINDER_KEY, String(Date.now()));

    const imgs = Array.from(d.querySelectorAll('[data-line-image] img')).slice(0, 3);
    const extra = Math.max(0, count - imgs.length);
    const remaining = parseInt(d.dataset.remaining || '0', 10);
    const currency = d.dataset.currency || 'GBP';
    const shipLine =
      remaining > 0
        ? `<span class="mono-label cart-toast__meta cart-toast__ship">ADD ${money(remaining, currency)} MORE FOR FREE UK SHIPPING</span>`
        : '';
    dismissReminder();

    toastEl = document.createElement('div');
    toastEl.className = 'cart-toast';
    toastEl.setAttribute('role', 'dialog');
    toastEl.setAttribute('aria-label', 'Cart reminder');
    toastEl.innerHTML = `
      <button type="button" class="cart-toast__x" data-toast-dismiss aria-label="Dismiss">&times;</button>
      <button type="button" class="cart-toast__main" data-toast-open>
        <span class="cart-toast__thumbs">
          ${imgs.map((img) => `<span class="cart-toast__thumb"><img src="${img.src}" alt="${(img.getAttribute('alt') || '').replace(/"/g, '&quot;')}"></span>`).join('')}
          ${extra > 0 ? `<span class="cart-toast__thumb cart-toast__thumb--extra">+${extra}</span>` : ''}
        </span>
        <span class="cart-toast__copy">
          <span class="cart-toast__title">STILL THINKING IT OVER?</span>
          <span class="mono-label cart-toast__meta">${count} ITEM${count === 1 ? '' : 'S'} &middot; ${d.dataset.subtotal}</span>
          ${shipLine}
        </span>
      </button>
      <button type="button" class="cart-toast__cta" data-toast-open>VIEW CART &rarr;</button>
    `;
    document.body.appendChild(toastEl);

    toastEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-toast-dismiss]')) {
        e.preventDefault();
        e.stopPropagation();
        dismissReminder();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      openDrawer();
    });

    setTimeout(() => dismissReminder(), 12000);
  };

  const scheduleReminder = () => {
    const d = getDrawer();
    if (!d || d.dataset.reminder !== 'true') return;
    clearTimeout(reminderTimer);
    const idleMs = parseInt(d.dataset.reminderIdle || '45', 10) * 1000;
    reminderTimer = setTimeout(fireReminder, idleMs);
  };

  document.addEventListener('DOMContentLoaded', () => {
    updateHeaderCount();
    applyUiBundle();
    scheduleReminder();
    ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach((evt) =>
      window.addEventListener(evt, scheduleReminder, { passive: true })
    );
  });

  document.addEventListener('shinplex:cart-updated', () => {
    updateHeaderCount();
    applyUiBundle();
  });

  window.ShinplexCart = {
    add: addToCart,
    open: openDrawer,
    refresh: refreshDrawer,
  };
})();

/* ============================================================
   Mobile menu drawer — ported from Lovable MobileMenu.tsx
   ============================================================ */
(() => {
  const menu = () => document.querySelector('[data-mobile-menu]');

  const open = () => {
    const m = menu();
    if (!m) return;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mobile-menu-open');
    document.querySelectorAll('[data-mobile-menu-open]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'true');
    });
  };

  const close = () => {
    const m = menu();
    if (!m) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mobile-menu-open');
    document.querySelectorAll('[data-mobile-menu-open]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (e) => {
    const shopToggle = e.target.closest('[data-shop-toggle]');
    if (shopToggle) {
      e.preventDefault();
      const grid = document.querySelector('[data-shop-grid]');
      const openShop = shopToggle.getAttribute('aria-expanded') !== 'true';
      shopToggle.setAttribute('aria-expanded', String(openShop));
      if (grid) grid.hidden = !openShop;
      return;
    }
    if (e.target.closest('[data-mobile-menu-open]')) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-mobile-menu-close]')) {
      close();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
})();

/* ============================================================
   Search dialog — ported from Lovable SearchDialog.tsx.
   Live results via Shopify predictive search (/search/suggest.json).
   ============================================================ */
(() => {
  const dialog = () => document.querySelector('[data-search-dialog]');
  let defaultHTML = null;
  let debounceTimer = null;
  let activeIndex = -1;

  const isOpen = () => {
    const d = dialog();
    return !!d && d.classList.contains('is-open');
  };

  const open = () => {
    const d = dialog();
    if (!d) return;
    if (defaultHTML === null) {
      defaultHTML = d.querySelector('[data-search-results]').innerHTML;
    }
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-open');
    const input = d.querySelector('[data-search-input]');
    input.value = '';
    d.querySelector('[data-search-results]').innerHTML = defaultHTML;
    activeIndex = -1;
    setTimeout(() => input.focus(), 50);
  };

  const close = () => {
    const d = dialog();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-open');
  };

  const items = () => Array.from(dialog().querySelectorAll('[data-search-item]'));

  const setActive = (i) => {
    const list = items();
    if (list.length === 0) return;
    activeIndex = ((i % list.length) + list.length) % list.length;
    list.forEach((el, idx) => el.classList.toggle('is-active', idx === activeIndex));
    list[activeIndex].scrollIntoView({ block: 'nearest' });
  };

  const renderResults = (data, query) => {
    const box = dialog().querySelector('[data-search-results]');
    const products = (data.resources.results.products || []);
    const pages = (data.resources.results.pages || []);
    const articles = (data.resources.results.articles || []);
    const collections = (data.resources.results.collections || []);
    let html = '';
    if (products.length) {
      html += '<div class="search-dialog__group-label mono-label">PRODUCTS</div>';
      html += products.map((p) => `
        <a href="${p.url}" class="search-item" data-search-item>
          <span class="search-item__img">${p.featured_image && p.featured_image.url ? `<img src="${p.featured_image.url}" alt="${String(p.title || '').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;')}" width="40" height="40">` : ''}</span>
          <span class="search-item__main">
            <span class="search-item__title">${p.title}</span>
            <span class="search-item__sub">PRODUCT</span>
          </span>
          <span class="search-item__price">${p.price ? '£' + p.price : ''}</span>
        </a>`).join('');
    }
    const links = [...collections, ...pages, ...articles];
    if (links.length) {
      html += '<div class="search-dialog__group-label mono-label">PAGES</div>';
      html += links.map((l) => `
        <a href="${l.url}" class="search-item search-item--page" data-search-item>
          <span class="search-item__title">${l.title}</span>
        </a>`).join('');
    }
    if (!html) {
      html = `
        <div class="search-dialog__empty">
          <div class="mono-label">NO MATCHES</div>
          <a href="/collections" class="search-dialog__browse">BROWSE ALL PRODUCTS &rarr;</a>
        </div>`;
    } else {
      html += `<a href="/search?q=${encodeURIComponent(query)}" class="search-item search-item--page" data-search-item>
        <span class="search-item__title">See all results for &ldquo;${query}&rdquo; &rarr;</span></a>`;
    }
    box.innerHTML = html;
    activeIndex = -1;
  };

  const runSearch = (query) => {
    if (!query.trim()) {
      dialog().querySelector('[data-search-results]').innerHTML = defaultHTML;
      activeIndex = -1;
      return;
    }
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article,collection&resources[limit]=6&resources[options][unavailable_products]=hide`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => renderResults(data, query))
      .catch(() => {});
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-search-close]')) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-search-input]')) return;
    clearTimeout(debounceTimer);
    const q = e.target.value;
    debounceTimer = setTimeout(() => runSearch(q), 200);
  });

  document.addEventListener('keydown', (e) => {
    if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      isOpen() ? close() : open();
      return;
    }
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(activeIndex - 1);
    } else if (e.key === 'Enter') {
      const list = items();
      if (activeIndex >= 0 && list[activeIndex]) {
        e.preventDefault();
        window.location.href = list[activeIndex].getAttribute('href');
      } else {
        const q = dialog().querySelector('[data-search-input]').value.trim();
        if (q) window.location.href = `/search?q=${encodeURIComponent(q)}`;
      }
    }
  });

  document.addEventListener('submit', (e) => {
    if (!e.target.matches('[data-cart-search-form]')) return;
    e.preventDefault();
    const seed = e.target.querySelector('[data-cart-search]');
    open();
    const input = dialog() && dialog().querySelector('[data-search-input]');
    if (input && seed && seed.value.trim()) {
      input.value = seed.value;
      runSearch(seed.value);
    }
  });
})();

/* FAQ page — turn Admin page.content (p>strong + ul) into Lovable accordion cards. */
(() => {
  const splitFaqSource = (root) => {
    if (!root) return;
    const nodes = [...root.childNodes].filter((n) => {
      if (n.nodeType === 3) return n.textContent.trim();
      return n.nodeType === 1;
    });
    const items = [];
    let current = null;
    const isQuestion = (el) => {
      if (!el || el.nodeType !== 1) return false;
      const strong = el.matches('strong') ? el : el.querySelector('strong');
      if (!strong) return false;
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      const q = (strong.textContent || '').replace(/\s+/g, ' ').trim();
      return q.length > 0 && text === q;
    };
    nodes.forEach((node) => {
      if (isQuestion(node)) {
        const strong = node.matches('strong') ? node : node.querySelector('strong');
        current = { q: strong.textContent.replace(/\s+/g, ' ').trim(), parts: [] };
        items.push(current);
        return;
      }
      if (current) current.parts.push(node);
    });
    if (!items.length) return;
    const frag = document.createDocumentFragment();
    items.forEach((item) => {
      const box = document.createElement('details');
      box.className = 'faq-box';
      const summary = document.createElement('summary');
      const q = document.createElement('span');
      q.className = 'faq-box__q';
      q.textContent = item.q;
      const toggle = document.createElement('span');
      toggle.className = 'mono-label faq-box__toggle';
      toggle.setAttribute('aria-hidden', 'true');
      toggle.textContent = '+';
      summary.append(q, toggle);
      const a = document.createElement('div');
      a.className = 'faq-box__a';
      item.parts.forEach((p) => a.appendChild(p));
      box.append(summary, a);
      frag.appendChild(box);
    });
    root.replaceWith(frag);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-faq-from-page]').forEach(splitFaqSource);
  });
})();

/* Press & Media mailto — native <a href="mailto:"> is ignored in some
   preview/browser contexts. Capture-phase click forces the mail handler. */
(() => {
  const PRESS_MAILTO = 'mailto:Team@shinplex.com?subject=Press';
  const isPressLink = (a) => {
    if (!a) return false;
    if (a.hasAttribute('data-press-mailto')) return true;
    if (a.classList.contains('press-mailto') || a.classList.contains('about-press-link')) return true;
    const href = (a.getAttribute('href') || '').trim();
    const text = (a.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return href.indexOf('mailto:') === 0 && text.indexOf('press') !== -1;
  };

  document.addEventListener(
    'click',
    (e) => {
      const a = e.target.closest('a');
      if (!isPressLink(a)) return;
      const href = (a.getAttribute('href') || '').trim() || PRESS_MAILTO;
      e.preventDefault();
      window.location.href = href.indexOf('mailto:') === 0 ? href : PRESS_MAILTO;
    },
    true
  );
})();

/* How to Mould walkthrough — click-to-play facade or native <video> controls. */
(() => {
  const mountIframe = (frame, src, title) => {
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = title || 'Moulding walkthrough';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('playsinline', '');
    frame.innerHTML = '';
    frame.appendChild(iframe);
  };

  document.addEventListener('click', (e) => {
    const play = e.target.closest('[data-mould-play]');
    if (play) {
      const frame = play.closest('.mould-video__frame');
      const src = (play.dataset.embed || '').trim();
      if (!frame || !src) return;
      e.preventDefault();
      mountIframe(frame, src, play.getAttribute('aria-label'));
      return;
    }

    const native = e.target.closest('[data-mould-native-play]');
    if (!native) return;
    const frame = native.closest('.mould-video__frame');
    const video = frame && frame.querySelector('video');
    if (!video) return;
    e.preventDefault();
    native.hidden = true;
    video.controls = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        video.controls = true;
      });
    }
  });
})();

/* Contact / club enquiry: if reCAPTCHA or another listener preventDefault and
   never finishes, native-submit after a short wait so SEND MESSAGE still posts. */
(() => {
  const contactSel = '#ContactPageForm, #ClubEnquiryForm, form.contact-form__inner';
  document.addEventListener('submit', (e) => {
    const form = e.target && e.target.closest && e.target.closest(contactSel);
    if (!form || form.getAttribute('data-contact-armed') === '1') return;
    form.setAttribute('data-contact-armed', '1');
    const btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
    window.setTimeout(() => {
      if (!document.body.contains(form)) return;
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
      HTMLFormElement.prototype.submit.call(form);
    }, 4000);
  });
})();

/* Hide Shopify Inbox over cart / Added to Bag / mobile bundle card. */
(() => {
  const hideSel = 'shopify-chat, #shopify-chat, #ShopifyChat, shop-login-button';
  const shouldHide = () => {
    const html = document.documentElement.classList;
    const body = document.body.classList;
    return (
      html.contains('cart-drawer-open') ||
      body.contains('cart-drawer-open') ||
      html.contains('added-sheet-open') ||
      body.contains('added-sheet-open') ||
      html.contains('pdp-kit-in-view') ||
      body.contains('pdp-kit-in-view')
    );
  };

  const applyChatHide = () => {
    const on = shouldHide();
    document.querySelectorAll(hideSel).forEach((el) => {
      if (on) {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      } else {
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('opacity');
        el.style.removeProperty('pointer-events');
      }
    });
  };

  window.__shinplexApplyChatHide = applyChatHide;

  const mo = new MutationObserver(applyChatHide);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  mo.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
  applyChatHide();
})();

/* Hide Shopify Chat on mobile while the Guards bundle card is on screen. */
(() => {
  const kit = document.querySelector('[data-pdp-kit]');
  if (!kit) return;

  const mq = window.matchMedia('(max-width: 767px)');
  const setInView = (on) => {
    document.documentElement.classList.toggle('pdp-kit-in-view', on);
    document.body.classList.toggle('pdp-kit-in-view', on);
  };

  const kitVisible = () => {
    const rect = kit.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    if (vh <= 0 || rect.height <= 0) return false;
    return rect.bottom > vh * 0.1 && rect.top < vh * 0.9;
  };

  const apply = () => {
    setInView(mq.matches && kitVisible());
  };

  if (window.IntersectionObserver) {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        setInView(mq.matches && visible);
      },
      { threshold: [0, 0.12, 0.4], rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(kit);
  }

  window.addEventListener('scroll', apply, { passive: true });
  window.addEventListener('resize', apply, { passive: true });
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply);
  apply();
  if (typeof window.__shinplexApplyChatHide === 'function') window.__shinplexApplyChatHide();
})();

/* Lift Inbox Chat + desktop Google Trust above the sticky ATC. */
(() => {
  const GAP = 18;
  const DESKTOP_MQ = '(min-width: 768px)';
  let gswRestBottom = null;

  const stickyEl = () => document.querySelector('[data-sticky-atc]');
  const gswEl = () => document.getElementById('google-merchantwidget-iframe-wrapper');

  const setChatOffset = (value) => {
    const nodes = [document.documentElement, document.querySelector('shopify-chat')].filter(Boolean);
    nodes.forEach((el) => {
      if (value) el.style.setProperty('--shopify-chat-activator-offset', value);
      else el.style.removeProperty('--shopify-chat-activator-offset');
    });
  };

  const applyFloatLift = () => {
    const sticky = stickyEl();
    const on = !!(
      document.body.classList.contains('pdp-sticky-on') &&
      sticky &&
      !sticky.hidden &&
      sticky.offsetHeight > 0
    );
    const lift = on ? Math.round(sticky.offsetHeight + GAP) + 'px' : '';
    setChatOffset(lift);

    const gsw = gswEl();
    if (!gsw) return;
    if (gswRestBottom == null) {
      gswRestBottom = gsw.style.getPropertyValue('bottom') || getComputedStyle(gsw).bottom || '46px';
    }
    if (on && window.matchMedia(DESKTOP_MQ).matches) {
      gsw.style.setProperty('bottom', lift, 'important');
    } else {
      gsw.style.setProperty('bottom', gswRestBottom, 'important');
    }
  };

  window.__shinplexApplyFloatLift = applyFloatLift;

  const mo = new MutationObserver(applyFloatLift);
  mo.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
  const sticky = stickyEl();
  if (sticky) mo.observe(sticky, { attributes: true, attributeFilter: ['hidden'] });
  window.addEventListener('resize', applyFloatLift, { passive: true });
  applyFloatLift();
})();
